/**
 * Pure helpers for 24-hour time entry.
 *
 * Only the 24-hour path is masked. A courier on 12-hour time keeps the native
 * <input type="time">, which already renders and edits correctly on the
 * 12-hour-locale browsers those couriers run — see TimePicker.
 *
 * The component exchanges 24-hour "HH:mm" strings with the rest of the app in
 * every direction; see dateToTimeString / timeStringToDate in
 * @src/common/utils/timezoneConverters.
 *
 * The colon is what makes editing safe. A buffer of bare digits is read as
 * shorthand by length ("930" -> 9:30), which is what someone typing a fresh
 * time means. The same rule applied to an edited value is dangerous:
 * backspacing "21:00" leaves "21:0", and reading those three digits as
 * shorthand would silently store 02:10. So whenever a colon is present the
 * split is positional — hours left of it, minutes right — and it survives
 * editing because the mask keeps the colon in the buffer.
 */

const MAX_DIGITS = 4;

interface TimeParts {
  hours: number;
  minutes: number;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9";
}

/**
 * Reduce raw input to what the mask accepts: at most four digits and at most
 * one colon, which may not lead. Everything else is dropped, so "9 PM" and
 * "21h00" collapse to digits while "21:0" keeps its structure.
 */
export function sanitizeDraft(raw: string): string {
  let draft = "";
  let digits = 0;
  let hasColon = false;

  for (const char of raw) {
    if (isDigit(char) && digits < MAX_DIGITS) {
      draft += char;
      digits += 1;
    } else if (char === ":" && !hasColon && draft.length > 0) {
      draft += char;
      hasColon = true;
    }
  }

  return draft;
}

/** Digits with no colon, read as shorthand: "9" -> 9:00, "930" -> 9:30. */
export function interpretDigits(digits: string): TimeParts | null {
  if (digits.length === 0) return null;

  if (digits.length <= 2) return { hours: Number(digits), minutes: 0 };
  if (digits.length === 3) {
    return { hours: Number(digits[0]), minutes: Number(digits.slice(1)) };
  }
  return { hours: Number(digits.slice(0, 2)), minutes: Number(digits.slice(2)) };
}

/**
 * Digits either side of a colon, read positionally: "21:0" is 21:00, not
 * 02:10. A half-typed minute pads to the right, so "21:3" is 21:30.
 */
function interpretAroundColon(draft: string, colonIndex: number): TimeParts | null {
  const hours = draft.slice(0, colonIndex);
  const minutes = draft.slice(colonIndex + 1);
  if (!hours) return null;

  return {
    hours: Number(hours),
    minutes: minutes ? Number(minutes.padEnd(2, "0")) : 0,
  };
}

/** Parse typed input into "HH:mm", or null if incomplete or out of range. */
export function parseTimeInput(raw: string): string | null {
  const draft = sanitizeDraft(raw);
  if (!draft) return null;

  const colonIndex = draft.indexOf(":");
  const parts =
    colonIndex === -1
      ? interpretDigits(draft)
      : interpretAroundColon(draft, colonIndex);

  if (!parts) return null;
  if (parts.hours > 23 || parts.minutes > 59) return null;

  return `${pad(parts.hours)}:${pad(parts.minutes)}`;
}

/** Normalize a stored value for display. Anything unparseable renders empty. */
export function formatTimeValue(value: string | null | undefined): string {
  if (!value) return "";

  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return "";

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return "";

  return `${pad(hours)}:${pad(minutes)}`;
}
